import { forwardRef, memo, useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { AIEmojisAPI } from '@makyoapp/makyo-sdk'
import { IconColOptionsOnly } from '@makyoapp/makyo-common'
import { MaterialIconGrade, MaterialIconOpSz, MaterialIconPickerBody, MaterialIconWeight } from '@makyoapp/makyo-ui'

interface IconEditorProps {
  value?: string
  settings?: IconColOptionsOnly
  onChange: (nextValue?: string) => void
  onCloseButtonClick?: () => void
}

export const IconEditor = memo(
  forwardRef<HTMLDivElement, IconEditorProps>(({ value, settings, onChange, onCloseButtonClick }, ref) => {
    const { icGrade, icTy, icWeight, icFill, icOpSz } = settings || {}
    const { t } = useTranslation()

    // Cache for search results
    const searchCache = useRef<Map<string, Promise<any>>>(new Map())

    const searchFilter = useCallback(async (keyword: string) => {
      // If keyword is empty, return empty result
      if (!keyword.trim()) {
        return []
      }

      // Check if we have a cached result for this keyword
      if (searchCache.current.has(keyword)) {
        return searchCache.current.get(keyword)
      }

      // If not cached, make the API call and cache the result
      const result = AIEmojisAPI.ReqFindEmoji(keyword)
      searchCache.current.set(keyword, result)

      return result
    }, [])

    const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
      // Prevent Enter key from closing the cell
      if (e.key === 'Enter') {
        e.stopPropagation()
        e.preventDefault()
      }
    }, [])

    return (
      <div ref={ref} className='text-m-m border bdr shrink-0 h-full' onKeyDown={handleKeyDown}>
        <div className='bg-zinc-50 dark:bg-slate-800 dark:text-zinc-50 h-full flex flex-col [&_.mk-modal-footer]:mt-auto'>
          <MaterialIconPickerBody
            onDone={(value) => {
              onChange(value)
            }}
            value={value || ''}
            title={t('Select Icon')}
            type={icTy}
            onClear={() => {
              onChange('')
            }}
            onClose={onCloseButtonClick}
            searchMs={300}
            searchFilter={searchFilter}
            clickAsDone={true}
            clearLabel={t('Clear')}
            doneLabel={t('Done')}
            settings={{
              fill: 0,
              grade: Number(icGrade) as MaterialIconGrade,
              opsz: Number(icOpSz) as MaterialIconOpSz,
              wght: Number(icWeight) as MaterialIconWeight,
            }}
          />
        </div>
      </div>
    )
  })
)

IconEditor.displayName = 'IconEditor'
